/* Main (basic) application styles */

import { image } from '@content';
import { createTheme } from '@material-ui/core/styles';
import { MuiPickersOverrides } from '@material-ui/pickers/typings/overrides';

import { variables } from './variables';

declare module '@material-ui/core/styles/createBreakpoints' {
  interface BreakpointOverrides {
    xs: false;
    sm: false;
    md: false;
    lg: true;
    xl: true;
  }
}

type OverridesNameToClassKey = {
  [P in keyof Required<MuiPickersOverrides>]: keyof MuiPickersOverrides[P];
};

declare module '@material-ui/core/styles/overrides' {
  export interface ComponentNameToClassKey extends OverridesNameToClassKey {
    [key: string]: unknown;
  }
}

const datePickerHighlightColor = `${variables.color.primary.green} !important`;

export const theme = createTheme({
  typography: {
    htmlFontSize: 10,
    fontFamily: [variables.font.family.base, variables.font.family.fallback].join(','),
  },
  overrides: {
    MuiPickersToolbar: {
      toolbar: {
        backgroundColor: datePickerHighlightColor,
      },
    },
    MuiPickersDay: {
      daySelected: {
        backgroundColor: datePickerHighlightColor,
      },
      current: {
        '&:not(.MuiPickersDay-daySelected)': {
          color: datePickerHighlightColor,
        },
      },
    },
    MuiPickersYear: {
      root: {
        '&:focus': {
          color: datePickerHighlightColor,
        },
      },
      yearSelected: {
        color: datePickerHighlightColor,
      },
    },
    MuiPickersCalendar: {
      transitionContainer: {
        marginBottom: '1.2rem',
      },
    },
    MuiCssBaseline: {
      '@global': {
        html: {
          fontSize: '62.5%',
          height: '100%',
          overflowY: 'hidden',
        },
        body: {
          margin: 0,
          fontFamily: variables.font.family.base,
          fontSize: variables.font.size.md,
          WebkitFontSmoothing: 'antialiased',
          height: '100%',
          width: '100vw',
          overflow: 'hidden',
        },
        '#root': {
          height: '100%',
        },
        '*::-webkit-scrollbar': {
          width: variables.scrollbar.width,
          height: variables.scrollbar.height,
        },
        '*::-webkit-scrollbar-thumb': {
          background: '#dfe5eb',
          borderRadius: variables.borderRadius.md,
        },
      },
    },
    MuiSelect: {
      root: {
        fontSize: variables.font.size.lg,
        padding: '0.8rem 1.2rem',
      },
    },
    MuiButton: {
      root: {
        '&:hover': {
          backgroundColor: 'none',
        },
      },
      contained: {
        backgroundColor: variables.colorV2.button.contained.primary.background.default,
        border: `0 solid ${variables.colorV2.button.contained.primary.border.default}`,
        borderRadius: '3.2rem',
        boxShadow: 'none',
        color: variables.colorV2.button.contained.primary.text.default,
        fontSize: '1.3rem',
        fontWeight: 400,
        height: '3.2rem',
        lineHeight: '3rem',
        minWidth: '3.2rem',
        padding: '0 1.6rem',
        textTransform: 'capitalize',
        '& svg path': {
          // fill: variables.colorV2.button.contained.primary.icon.fill.default,
          stroke: variables.colorV2.button.contained.primary.icon.stroke.default,
        },

        '&:hover': {
          backgroundColor: variables.colorV2.button.contained.primary.background.hover,
          border: `0 solid ${variables.colorV2.button.contained.primary.border.hover}`,
          color: variables.colorV2.button.contained.primary.text.hover,
          boxShadow: 'none',
          '& svg path': {
            // fill: variables.colorV2.button.contained.primary.icon.fill.hover,
            stroke: variables.colorV2.button.contained.primary.icon.stroke.hover,
          },
        },
        '&$focusVisible': {
          boxShadow: 'none',
        },
        '&:active': {
          border: `0 solid ${variables.colorV2.button.contained.primary.border.active}`,
          backgroundColor: variables.colorV2.button.contained.primary.background.active,
          color: variables.colorV2.button.contained.primary.text.active,
          boxShadow: 'none',

          '& svg path': {
            // fill: variables.colorV2.button.contained.primary.icon.fill.active,
            stroke: variables.colorV2.button.contained.primary.icon.stroke.active,
          },
        },
        '&:disabled': {
          backgroundColor: variables.colorV2.button.disabled.background.default,
          color: variables.colorV2.button.disabled.text.default,
        },
      },
      containedPrimary: {
        backgroundColor: variables.colorV2.button.contained.primary.background.default,
        '&:hover': {
          backgroundColor: variables.colorV2.button.contained.primary.background.hover,
          border: `0 solid ${variables.colorV2.button.contained.primary.border.hover}`,
          color: variables.colorV2.button.contained.primary.text.hover,
          boxShadow: 'none',
        },
        '&:active': {
          border: `0 solid ${variables.colorV2.button.contained.primary.border.active}`,
          backgroundColor: variables.colorV2.button.contained.primary.background.active,
          color: variables.colorV2.button.contained.primary.text.active,
          boxShadow: 'none',
        },
      },
      containedSecondary: {
        // padding: `0.2rem ${variables.gap.lg} 0.1rem`,
        backgroundColor: variables.colorV2.button.contained.secondary.background.default,
        '& svg path': {
          // fill: variables.colorV2.button.contained.secondary.icon.fill.default,
          stroke: variables.colorV2.button.contained.secondary.icon.stroke.default,
        },

        '&:hover': {
          backgroundColor: variables.colorV2.button.contained.secondary.background.hover,
          border: `0 solid ${variables.colorV2.button.contained.secondary.border.hover}`,
          color: variables.colorV2.button.contained.secondary.text.hover,
          boxShadow: 'none',
          '& svg path': {
            // fill: variables.colorV2.button.contained.secondary.icon.fill.hover,
            stroke: variables.colorV2.button.contained.secondary.icon.stroke.hover,
          },
        },
        '&$focusVisible': {
          boxShadow: 'none',
        },
        '&:active': {
          border: `0 solid ${variables.colorV2.button.contained.secondary.border.active}`,
          backgroundColor: variables.colorV2.button.contained.secondary.background.active,
          color: variables.colorV2.button.contained.secondary.text.active,
          boxShadow: 'none',

          '& svg path': {
            // fill: variables.colorV2.button.contained.secondary.icon.fill.active,
            stroke: variables.colorV2.button.contained.secondary.icon.stroke.active,
          },
        },
      },
      outlined: {
        backgroundColor: variables.colorV2.button.outlined.primary.background.default,

        borderRadius: '3.2rem', // '.8rem',
        lineHeight: variables.lineHeight.md,
        letterSpacing: variables.letterSpacing.lg,
        fontWeight: 400, // variables.font.weight.bold,
        fontSize: variables.font.size.md,
        textTransform: 'capitalize',
      },
      outlinedPrimary: {
        border: `.1rem solid ${variables.colorV2.button.outlined.primary.border.default}`,
        color: variables.colorV2.button.outlined.primary.text.default,
        padding: '0.6rem 1.2rem',

        '&:hover, &:active': {
          border: `.1rem solid ${variables.colorV2.button.outlined.primary.border.hover}`,
          color: variables.colorV2.button.outlined.primary.text.hover,
          '& svg path': {
            stroke: variables.colorV2.button.outlined.primary.icon.stroke.hover,
          },
        },
        '&:hover': {
          backgroundColor: variables.colorV2.button.outlined.primary.background.hover,
        },
        '&:active': {
          backgroundColor: variables.colorV2.button.outlined.primary.background.active,
          border: `.1rem solid ${variables.colorV2.button.outlined.primary.border.active}`,
          // boxShadow: 'inset 0 0.2rem 0.2rem rgba(0, 0, 0, 0.16)',
        },
      },
      outlinedSecondary: {
        border: `${variables.borderWidth.sm} solid ${variables.color.primary.mainPurple}`,
        color: variables.color.primary.mainPurple,
        padding: '0.6rem 1.2rem',
        backgroundColor: variables.color.primary.backgroundLightGray,
        '&:hover': {
          backgroundColor: 'transparent',
          border: `${variables.borderWidth.sm} solid ${variables.color.secondary.blue}`,
          color: variables.color.secondary.blue,
        },
      },
      text: {
        paddingTop: variables.gap.xxs,
        paddingBottom: variables.gap.xxs,
        textTransform: 'capitalize',
        fontWeight: variables.font.weight.bold,
        borderRadius: variables.borderRadius.sm,

        '&:disabled': {
          opacity: variables.opacity.medium,
        },
      },
    },
    MuiIconButton: {
      root: {
        '&:hover': {
          backgroundColor: 'transparent !important',
        },
      },
    },
    MuiLink: {
      root: {
        color: variables.colorV2.link.text.default,
        fontSize: variables.font.size.md,
        fontWeight: variables.font.weight.bold,
        lineHeight: variables.lineHeight.md,
        letterSpacing: '0.06rem',
        '&:hover': {
          color: variables.colorV2.link.text.hover,
        },
      },
    },
    MuiFormControlLabel: {
      label: {
        fontSize: variables.font.size.md,
        fontWeight: variables.font.weight.semiBold,
        color: variables.color.primary.darkestGray,
        lineHeight: variables.lineHeight.lg,

        '&$disabled': {
          color: variables.color.primary.lightGrayDarker,
        },
      },
    },
    MuiOutlinedInput: {
      input: {
        padding: '0.8rem 1.2rem',
      },
      inputAdornedStart: {
        paddingLeft: '0.8rem',
      },
      notchedOutline: {
        border: 'none',
      },
    },
    MuiCheckbox: {
      root: {
        color: variables.colorV2.input.default.text.default,
        paddingRight: 8,
        transform: 'scale(0.889)',

        '&$checked': {
          color: variables.colorV2.input.checkbox.background.active,

          '& svg': {
            background: `url(${image.checkboxChecked}) center no-repeat`,
          },
          '& svg path': {
            display: 'none',
          },
        },

        '&$disabled': {
          color: variables.color.primary.lightGray,
        },

        '&$checked$disabled': {
          '& svg': {
            background: `url(${image.checkboxCheckedDisabled}) center no-repeat`,
          },
        },

        '&.MuiCheckbox-indeterminate': {
          color: variables.color.primary.mainPurple,
        },
      },
    },
    MuiRadio: {
      root: {
        color: variables.colorV2.input.radio.bullet.default,
        paddingRight: 8,
        transform: 'scale(0.8)',

        '&$checked': {
          color: `${variables.colorV2.input.radio.bullet.active} !important`,
        },

        '&$disabled': {
          color: variables.color.primary.lightGray,
        },
      },
      checked: {},
    },
    MuiSwitch: {
      root: {
        width: '4rem',
        height: '2rem',
        padding: 0,
      },
      switchBase: {
        padding: '0.2rem',
        color: variables.color.primary.white,
        '& + $track': {
          backgroundColor: variables.color.primary.lightGrayDarker,
        },
        '&$checked': {
          color: variables.color.primary.white,
          transform: 'translateX(2rem)',
          '& + $track': {
            opacity: 1,
            backgroundColor: variables.colorV2.button.default.background.default,
          },
        },
        '&$disabled': {
          color: variables.colorV2.button.disabled.text.default,
          '&$checked': {
            transform: 'translateX(2rem)',
          },
          '& + $track': {
            opacity: 1,
            backgroundColor: `${variables.color.primary.lightGrayDarker} !important`,
          },
        },
      },
      thumb: {
        width: '1.6rem',
        height: '1.6rem',
        boxShadow: 'none',
      },
      track: {
        borderRadius: '2rem',
        opacity: 1,
        background:
          "#fff url(\"data:image/svg+xml;utf8,<svg width='10' height='8' viewBox='0 0 10 8' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M9.49993 2.41414C9.89045 2.02361 9.89045 1.39045 9.49992 0.999924C9.1094 0.6094 8.47623 0.6094 8.08571 0.999924L3.49992 5.58571L1.91414 3.99993C1.52361 3.6094 0.890449 3.6094 0.499924 3.99992C0.1094 4.39045 0.1094 5.02361 0.499924 5.41414L2.79282 7.70703C3.18334 8.09756 3.81651 8.09756 4.20703 7.70703L9.49993 2.41414Z' fill='white'/></svg>\") 5px center no-repeat",
      },
      checked: {},
    },
    MuiInput: {
      root: {
        backgroundColor: variables.colorV2.input.default.background.default,
        border: `1px solid ${variables.colorV2.input.default.border.default}`,
        borderRadius: '.8rem',
        color: variables.colorV2.input.default.text.default,
        fontSize: '1.3rem',
        fontWeight: 500,
        height: '3.2rem',
        padding: '0.8rem 1.2rem',

        '& ::placeholder': {
          color: variables.colorV2.input.default.text.placeholder,
        },
      },
      input: {
        lineHeight: variables.lineHeight.lg,
        padding: '1px 0',
        height: 'auto',
      },
    },
    MuiInputBase: {
      root: {
        padding: '0.2rem 1.2rem',
        border: `0.01rem solid ${variables.color.primary.lightGray}`,
        borderRadius: variables.borderRadius.sm,
        fontWeight: variables.font.weight.semiBold,
        fontSize: variables.font.size.md,
        color: variables.color.primary.darkestGray,
        lineHeight: variables.lineHeight.md,

        '& ::placeholder': {
          color: variables.colorV2.input.default.text.default,
        },
      },
    },
    MuiInputLabel: {
      root: {
        color: variables.color.primary.darkestGray,
        fontSize: variables.font.size.md,
        fontWeight: variables.font.weight.semiBold,
        marginBottom: variables.gap.xs,
      },
    },
    MuiTextField: {
      root: {
        '& .MuiInputBase-root': {
          color: variables.colorV2.input.default.text.default,
          fontSize: variables.font.size.md,
        },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
          borderBottom: 'none',
        },
        '& .MuiInput-underline:after': {
          borderBottom: 'none',
        },
        '& .MuiInput-underline.Mui-focused:after': {
          borderBottom: 'none',
        },
        '& .MuiInput-underline.Mui-error::after': {
          borderBottom: 'none',
        },
        '& .MuiFormHelperText-root.Mui-error': {
          color: variables.color.secondary.orange,
          fontSize: variables.font.size.sm,
          fontWeight: variables.font.weight.bold,
          lineHeight: variables.lineHeight.sm,
          letterSpacing: variables.letterSpacing.sm,
        },
        '& .Mui-focused': {
          backgroundColor: variables.colorV2.input.default.background.active,
        },
        '& .MuiInput-underline::before': {
          borderBottom: 'none',
        },
        '& svg > path': {
          stroke: variables.colorV2.input.default.icon.fill.default,
        },
      },
    },
    MuiAutocomplete: {
      inputRoot: {
        paddingTop: '0.1rem',
      },
    },
    MuiCircularProgress: {
      colorPrimary: {
        color: variables.color.primary.green,
      },
    },
    MuiTable: {
      root: {
        borderSpacing: '0 0.5rem',
        borderCollapse: 'separate',
        marginTop: '-0.5rem',
      },
    },
    MuiTableRow: {
      head: {
        background: variables.color.primary.white,
      },
    },
    MuiTableCell: {
      root: {
        borderBottom: 0,
        padding: variables.gap.lg,
      },
      body: {
        background: variables.colorV2.body.background.default,
        height: '12rem',
        wordBreak: 'break-word',
        '&:first-child': {
          borderTopLeftRadius: variables.borderRadius.sm,
          borderBottomLeftRadius: variables.borderRadius.sm,
        },
        '&:last-child': {
          borderBottomRightRadius: variables.borderRadius.sm,
          borderTopRightRadius: variables.borderRadius.sm,
        },
      },
    },
    MuiMenuItem: {
      root: {
        fontSize: variables.font.size.md,
        fontWeight: variables.font.weight.bold,
        lineHeight: variables.lineHeight.lg,
        color: variables.colorV2.dropdown.list.text.default,
        borderRadius: variables.borderRadius.sm,
        minHeight: 'initial',

        '&:hover': {
          color: variables.colorV2.dropdown.list.text.hover,
          backgroundColor: variables.colorV2.dropdown.list.background.hover,
        },
      },
    },
    MuiAccordion: {
      rounded: {
        '&:first-child': {
          borderTopLeftRadius: variables.borderRadius.sm,
          borderTopRightRadius: variables.borderRadius.sm,
        },
        '&:last-child': {
          borderBottomLeftRadius: variables.borderRadius.sm,
          borderBottomRightRadius: variables.borderRadius.sm,
        },
      },
    },
    MuiAccordionSummary: {
      root: {
        padding: '2.4rem',

        '&$expanded': {
          borderBottom: `${variables.borderWidth.sm} solid ${variables.color.primary.lightGray}`,
        },
      },
      expandIcon: {
        marginRight: 0,
        backgroundImage: `url(${image.accordionExpand})`,

        '&$expanded': {
          backgroundImage: `url(${image.accordionCollapse})`,
        },
      },
      content: {
        alignItems: 'center',
        margin: 0,
        minHeight: '4.4rem',

        '&$expanded': {
          margin: 0,
        },
      },
    },
    MuiAccordionDetails: {
      root: {
        padding: '1.6rem 1.6rem 0 1.6rem',
      },
    },
    MuiDivider: {
      root: {
        backgroundColor: variables.color.primary.lightGray,
      },
    },
  },
  breakpoints: {
    values: {
      lg: 1072,
      xl: 1346,
    },
  },
  props: {
    MuiButtonBase: {
      disableRipple: true, // No more ripple, on the whole application 💣!
    },
  },
});
